import React, { useState, useEffect, useCallback } from 'react';
import { Star } from 'lucide-react';

const ClientTestimonial = () => {
  const testimonials = [
    {
      id: 1,
      text: "I was so impressed with the professionalism and quality of work from TM-International Construction. They recently completed a kitchen renovation for me and it turned out even better than I had hoped. The attention to detail and commitment to excellence was evident throughout the entire project. I would highly recommend TM-International Construction to anyone looking for a reliable and skilled contractor.",
      author: "Jessica Williams",
      role: "Homeowner"
    },
    {
      id: 2,
      text: "I had the pleasure of working with TM Construction on a commercial flooring project and was blown away by their expertise and professionalism. The team was able to recommend the perfect solution for our needs and completed the installation with speed and precision. Their project management was exceptional, keeping us informed every step of the way. I will definitely be using TM-International Construction for any future projects.",
      author: "Daniel Lee",
      role: "Business Owner"
    },
    {
      id: 3,
      text: "I had TM-International Construction remodel my bathroom and it was a great experience from start to finish. The team was able to turn my outdated bathroom into a beautiful, modern space that I love. The attention to detail was impressive, and the quality of work was outstanding. They completed the project on time and within budget, which exceeded my expectations. I am so happy with the final result and would highly recommend them to anyone.",
      author: "Alex Kim",
      role: "Homeowner"
    },
    {
      id: 4,
      text: "TM Construction completed a full renovation of my home and I could not be happier with the results. They were able to create a design that fit my needs perfectly and executed it flawlessly. The team was always professional, friendly, and available to answer any questions I had. Their craftsmanship is second to none, and I appreciate their commitment to customer satisfaction.",
      author: "Amanda Brown",
      role: "Homeowner"
    },
    {
      id: 5,
      text: "They did an exceptional job with my commercial flooring installation. The team was knowledgeable, efficient, and the end result exceeded my expectations. They were able to recommend the best flooring options for my business based on traffic patterns and durability requirements, and completed the installation in a timely manner. Their professionalism throughout the project was exemplary.",
      author: "Ethan Chen",
      role: "Restaurant Owner"
    },
    {
      id: 6,
      text: "I recently had them install new windows in my home and I couldn't be happier with the results. The team was knowledgeable and friendly, and they made sure to take care of every detail from start to finish. They were able to complete the installation in a timely manner without disrupting my daily routine, and the quality of work was outstanding. My energy bills have decreased significantly since the new windows were installed.",
      author: "Jennifer Nguyen",
      role: "Homeowner"
    },
    {
      id: 7,
      text: "TM-International Construction did an amazing job on my basement renovation. They were able to transform the space into a functional and beautiful living area that my family now enjoys every day. The team was professional and reliable, and they always made sure to clean up after themselves at the end of each day. They were able to complete the project on time and within budget, which was greatly appreciated. The craftsmanship is exceptional.",
      author: "Christopher Lee",
      role: "Homeowner"
    },
    {
      id: 8,
      text: "I recently had TM-International Construction build a custom deck for my backyard and I couldn't be happier with the results. The team was able to create a design that fit my needs perfectly and executed it with precision. They were always available to answer any questions I had and kept me informed throughout the process. The deck has become our favorite outdoor space for entertaining and relaxation.",
      author: "Sophia Chen",
      role: "Homeowner"
    }
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(1);
  const [isPaused, setIsPaused] = useState(false);

  // Handle responsive items per page
  useEffect(() => {
    const handleResize = () => {
      // Show 2 items on md/lg screens, 1 on mobile
      const newItemsPerPage = window.innerWidth >= 768 ? 2 : 1;
      setItemsPerPage(newItemsPerPage);
    };

    // Set initial value
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset index if itemsPerPage changes to avoid out-of-bounds
  useEffect(() => {
    setCurrentIndex(0);
  }, [itemsPerPage]);

  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalPages);
  }, [totalPages]);

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + totalPages) % totalPages);
  };

  // Auto slide effect
  useEffect(() => {
    if (!isPaused) {
      const timer = setInterval(() => {
        nextSlide();
      }, 3000); // 3 seconds
      return () => clearInterval(timer);
    }
  }, [isPaused, nextSlide]);

  return (
    <div className="bg-gray-50 py-12 md:py-16 font-sans">
      <div className="max-w-6xl mx-auto text-center" style={{ fontFamily: "Monstserrat, proxima-nova, Helveti, sans-serif" }}>

        {/* Main Header */}
        <h2 className="text-4xl md:text-5xl text-slate-800 mb-6 tracking-wide font-semibold uppercase">
          Client Testimonials
        </h2>

        {/* Intro Text */}
        <p className="max-w-3xl mx-auto text-base md:text-lg text-slate-600 mb-10 md:mb-14 font-light leading-relaxed px-2">
          Our commitment to excellence is reflected in the satisfaction of our clients.
          Here are testimonials from those who have experienced the TM International difference:
        </p>

        {/* Slider Container */}
        <div
          className="relative max-w-6xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-12 z-20 bg-white hover:bg-slate-50 text-slate-700 p-2 md:p-3 rounded-full shadow-lg border border-slate-100 transition-all duration-200 group focus:outline-none"
            aria-label="Previous slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 md:w-6 md:h-6 group-hover:-translate-x-0.5 transition-transform">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-12 z-20 bg-white hover:bg-slate-50 text-slate-700 p-2 md:p-3 rounded-full shadow-lg border border-slate-100 transition-all duration-200 group focus:outline-none"
            aria-label="Next slide"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 md:w-6 md:h-6 group-hover:translate-x-0.5 transition-transform">
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>

          {/* Carousel Track Wrapper */}
          <div className="overflow-hidden px-1 py-4"> {/* Added py-4 to accommodate shadow */}
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {testimonials.map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="min-w-full md:min-w-[50%] px-3 md:px-4 flex"
                >
                  <div className="bg-gradient-to-br from-white via-white to-slate-100 p-6 md:p-8 shadow-sm border border-slate-100 flex flex-col w-full h-full hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    {/* Quote Icon */}
                    <div className="text-slate-300 mb-4">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14.017 21L14.017 18C14.017 16.896 14.321 16.067 14.932 15.513C15.539 14.96 16.518 14.654 17.87 14.595L18.182 14.595L18.182 8.794L16.273 8.794C15.011 8.794 14.152 8.941 13.696 9.233C13.245 9.53 12.871 10.05 12.574 10.794L11.536 10.794L11.536 3L22 3L22 21L14.017 21ZM5 21L5 18C5 16.896 5.304 16.067 5.915 15.513C6.522 14.96 7.501 14.654 8.853 14.595L9.165 14.595L9.165 8.794L7.256 8.794C5.994 8.794 5.135 8.941 4.679 9.233C4.228 9.53 3.854 10.05 3.557 10.794L2.519 10.794L2.519 3L13 3L13 21L5 21Z" />
                      </svg>
                    </div>

                    {/* Review Text */}
                    <p className="text-slate-600 font-light leading-relaxed mb-6 flex-grow italic text-left text-sm md:text-base">
                      "{testimonial.text}"
                    </p>

                    {/* Author Details */}
                    <div className="mt-auto border-t border-slate-200/60 pt-4 text-left">
                      <p className="text-slate-800 font-medium text-lg">
                        {testimonial.author}
                      </p>
                      <p className="text-slate-500 text-sm font-light uppercase tracking-wide">
                        {testimonial.role}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Slide Indicators */}
          <div className="flex justify-center space-x-2 mt-6 md:mt-8">
            {Array.from({ length: totalPages }).map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-2 h-2 md:w-2.5 md:h-2.5 rounded-full transition-all duration-300 ${currentIndex === idx ? 'bg-slate-700 w-4 md:w-6' : 'bg-slate-300 hover:bg-slate-400'
                  }`}
                aria-label={`Go to slide ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Review CTA Section */}
        <div className="mt-12 md:mt-16 max-w-4xl mx-auto">
          <div className="bg-[#111827] rounded-2xl p-8 md:p-10 text-center text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Had a Great Experience? Share It!
            </h3>
            <p className="text-lg text-blue-100 mb-6 max-w-2xl mx-auto">
              Your review helps us improve and helps other homeowners find quality construction services they can trust.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => window.open('https://share.google/UZRWAdHEcK7WWPqNJ')}
                className="flex items-center justify-center gap-2 bg-white text-[#2a3890] px-6 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors text-sm md:text-base"
              >
                {/* Icon aur text ab ek line mein honge */}
                <Star className="w-5 h-5 fill-current" />
                <span>Review on Google</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientTestimonial;